import { useState } from "react";
import { Crown, Play, Plus, Save, Download, Upload } from "lucide-react";
import { useStoredAuth } from "@/hooks/useStoredAuth";
import PremiumLock from "./PremiumLock";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function NotebooksSection() {
  const { user, isAuthenticated } = useStoredAuth();
  const hasAccess = isAuthenticated && user?.isPremium;
  const [activeNotebook, setActiveNotebook] = useState("gpt2-example");
  const [codeContent, setCodeContent] = useState(`import torch
import transformers
from transformers import GPT2LMHeadModel, GPT2Tokenizer

# Cargar modelo pre-entrenado
model_name = "gpt2"
tokenizer = GPT2Tokenizer.from_pretrained(model_name)
model = GPT2LMHeadModel.from_pretrained(model_name)

# Generar texto
prompt = "La inteligencia artificial es"
inputs = tokenizer.encode(prompt, return_tensors="pt")
outputs = model.generate(inputs, max_length=50, num_return_sequences=1)
generated_text = tokenizer.decode(outputs[0], skip_special_tokens=True)
print(generated_text)`);
  const [output, setOutput] = useState("Modelo cargado exitosamente");
  const [isRunning, setIsRunning] = useState(false);

  const notebooks = [
    { id: "gpt2-example", name: "GPT-2 Text Generation", description: "Generación de texto con GPT-2" },
    { id: "image-classification", name: "Image Classification", description: "Clasificación de imágenes con CNN" },
    { id: "sentiment-analysis", name: "Sentiment Analysis", description: "Análisis de sentimientos con BERT" },
  ];

  const runCode = async () => {
    if (!hasAccess) return;
    
    setIsRunning(true);
    setOutput("Ejecutando código...");
    
    // Simular ejecución de código
    setTimeout(() => {
      setOutput(`La inteligencia artificial es una tecnología revolucionaria que está transformando el mundo de maneras increíbles. Desde el procesamiento de lenguaje natural hasta la visión por computadora, la IA está creando nuevas posibilidades.

Ejecución completada en 2.3 segundos.`);
      setIsRunning(false);
    }, 2000);
  };

  const saveNotebook = () => {
    if (!hasAccess) return;
    // Simular guardado
    alert("Notebook guardado exitosamente");
  };

  const createNewNotebook = () => {
    if (!hasAccess) return;
    const newName = prompt("Nombre del nuevo notebook:");
    if (newName) {
      setCodeContent("# Nuevo notebook\nprint('¡Hola mundo!')");
      setOutput("");
    }
  };

  return (
    <section id="notebooks" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-2">Notebooks IA</h3>
            <p className="text-lg text-gray-600">Entorno de desarrollo interactivo para experimentar con IA</p>
          </div>
          {!hasAccess && (
            <span className="bg-amber-400 text-white px-4 py-2 rounded-full font-semibold flex items-center">
              <Crown className="mr-2 w-4 h-4" />
              Premium
            </span>
          )}
        </div>

        <div className="relative">
          {!hasAccess && (
            <PremiumLock
              title="Notebooks Premium"
              description="Accede a nuestro entorno de notebooks para crear y experimentar con IA"
            />
          )}

          <div className={`${!hasAccess ? 'filter blur-sm' : ''}`}>
            {hasAccess && (
              <div className="mb-6">
                <div className="flex flex-wrap gap-3 mb-4">
                  <Button onClick={createNewNotebook} className="bg-blue-600 text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Notebook
                  </Button>
                  <Button onClick={saveNotebook} variant="outline">
                    <Save className="w-4 h-4 mr-2" />
                    Guardar
                  </Button>
                  <Button variant="outline">
                    <Upload className="w-4 h-4 mr-2" />
                    Subir
                  </Button>
                  <Button variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    Descargar
                  </Button>
                </div>

                <Tabs value={activeNotebook} onValueChange={setActiveNotebook}>
                  <TabsList className="grid w-full grid-cols-3">
                    {notebooks.map((notebook) => (
                      <TabsTrigger key={notebook.id} value={notebook.id}>
                        {notebook.name}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  
                  {notebooks.map((notebook) => (
                    <TabsContent key={notebook.id} value={notebook.id}>
                      <Card>
                        <CardContent className="p-0">
                          <div className="bg-gray-900 rounded-t-lg">
                            <div className="flex items-center justify-between p-4 border-b border-gray-700">
                              <div className="flex items-center space-x-3">
                                <div className="flex space-x-2">
                                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                </div>
                                <span className="text-gray-400 text-sm">{notebook.name}.ipynb</span>
                              </div>
                              <Button
                                onClick={runCode}
                                disabled={isRunning}
                                className="bg-emerald-600 text-white text-sm px-3 py-1"
                              >
                                <Play className="w-3 h-3 mr-1" />
                                {isRunning ? "Ejecutando..." : "Ejecutar"}
                              </Button>
                            </div>
                          </div>

                          <div className="bg-gray-900">
                            {/* Code Cell */}
                            <div className="border-l-4 border-blue-500 bg-gray-800 m-4 rounded">
                              <div className="flex items-center p-2 bg-gray-700 rounded-t">
                                <span className="text-blue-400 text-sm font-mono">In [1]:</span>
                              </div>
                              <div className="p-4">
                                <textarea
                                  value={codeContent}
                                  onChange={(e) => setCodeContent(e.target.value)}
                                  className="w-full bg-transparent text-green-400 font-mono text-sm resize-none border-none outline-none"
                                  rows={12}
                                  placeholder="Escribe tu código aquí..."
                                />
                              </div>
                            </div>

                            {/* Output Cell */}
                            {output && (
                              <div className="border-l-4 border-orange-500 bg-gray-800 m-4 rounded">
                                <div className="flex items-center p-2 bg-gray-700 rounded-t">
                                  <span className="text-orange-400 text-sm font-mono">Out [1]:</span>
                                </div>
                                <div className="p-4">
                                  <pre className="text-gray-300 text-sm font-mono whitespace-pre-wrap">
                                    {output}
                                  </pre>
                                </div>
                              </div>
                            )}

                            {/* Markdown Cell */}
                            <div className="border-l-4 border-purple-500 bg-gray-800 m-4 rounded">
                              <div className="p-4">
                                <h4 className="text-white font-semibold mb-2">📝 Descripción</h4>
                                <p className="text-gray-300 text-sm">
                                  {notebook.description}: Este notebook te permite experimentar con modelos de IA de forma interactiva. 
                                  Puedes modificar el código, ejecutarlo y ver los resultados en tiempo real.
                                </p>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            )}

            {/* Vista previa para usuarios no premium */}
            {!hasAccess && (
              <div className="bg-gray-900 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>
                  <span className="text-gray-400 ml-4 text-sm">notebook_ia_ejemplo.ipynb</span>
                </div>
                
                <div className="bg-gray-800 rounded-lg p-4 mb-4">
                  <div className="flex items-center mb-2">
                    <span className="text-orange-400 text-sm">In [1]:</span>
                  </div>
                  <pre className="text-green-400 text-sm font-mono">
{`import torch
import transformers
from transformers import GPT2LMHeadModel, GPT2Tokenizer

# Cargar modelo pre-entrenado
model_name = "gpt2"
tokenizer = GPT2Tokenizer.from_pretrained(model_name)
model = GPT2LMHeadModel.from_pretrained(model_name)`}
                  </pre>
                </div>

                <div className="bg-gray-800 rounded-lg p-4 mb-4">
                  <div className="flex items-center mb-2">
                    <span className="text-orange-400 text-sm">Out [1]:</span>
                  </div>
                  <pre className="text-gray-300 text-sm">Modelo cargado exitosamente</pre>
                </div>

                <div className="bg-gray-800 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-2">Generación de Texto con GPT-2</h4>
                  <p className="text-gray-300 text-sm">
                    En este notebook exploramos cómo usar GPT-2 para generar texto coherente...
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
